/**
 * Préchargement des chunks de route au survol des liens de navigation.
 *
 * React Router 7 en mode `<Routes>` (non « data router ») n'expose pas de
 * mécanisme de préchargement natif. On mappe donc chaque destination de la
 * sidebar vers le MÊME thunk d'import dynamique que celui passé à `React.lazy`
 * dans `App.tsx`. Vite/Rollup dédoublonnent les chunks par graphe de modules :
 * précharger ici, c'est charger d'avance exactement le chunk que la navigation
 * demanderait au clic — mais pendant que la souris survole encore le lien.
 *
 * Effet : au clic, le module est déjà en cache → pas de suspension → la
 * navigation paraît instantanée ET le shell (sidebar/topbar/bulles) ne
 * clignote pas.
 *
 * Idempotent : réappeler un import déjà résolu est un no-op (cache de modules
 * ESM). On mémorise en plus les chemins déjà préchargés pour éviter tout appel
 * superflu.
 */

// Préfixe de path sidebar → thunk d'import du 1er écran de la section.
// IMPORTANT : les specifiers doivent être IDENTIQUES à ceux de `App.tsx` pour
// que Rollup produise un chunk commun (sinon on précharge un doublon inutile).
const PREFETCHERS: Record<string, () => Promise<unknown>> = {
  "/vignoble": () => import("../pages/Vignoble.js"),
  "/vendange": () => import("../pages/vendange/Vendange.js"),
  "/cuverie": () => import("../pages/cuverie/Cuverie.js"),
  "/cuverie/fermentations": () => import("../pages/cuverie/fermentations/index.js"),
  "/cave": () => import("../pages/cave/CaveHub.js"),
  "/stock": () => import("../pages/stock/index.js"),
  "/stock/inventaires": () => import("../pages/stock/inventaires/index.js"),
  "/stock-cols": () => import("../pages/stock-cols/index.js"),
  "/etiquetage": () => import("../pages/etiquetage/index.js"),
  "/ventes": () => import("../pages/ventes/HubVentes.js"),
  "/conformite": () => import("../pages/conformite/HubConformite.js"),
  "/boutique-admin": () => import("../pages/boutique-admin/index.js"),
  "/parametres": () => import("../pages/Parametres.js"),
  "/aide": () => import("../pages/aide/AidePage.js"),
};

// Chemins déjà préchargés dans la session (évite de rappeler le thunk).
const dejaPrefetch = new Set<string>();

/**
 * Précharge le chunk associé à `path` s'il est connu et pas déjà chargé.
 * À brancher sur `onMouseEnter` / `onFocus` des liens de navigation.
 */
export function prefetchRoute(path: string): void {
  const prefetcher = PREFETCHERS[path];
  if (!prefetcher || dejaPrefetch.has(path)) return;
  dejaPrefetch.add(path);
  // Best-effort : en cas d'échec réseau, on oublie le marquage pour laisser
  // la navigation réelle réessayer (et gérer proprement l'erreur de chunk).
  void prefetcher().catch(() => {
    dejaPrefetch.delete(path);
  });
}
